import { writeFetch, getSession } from '../api';

/* ─────────────────────────────────────────────────────────────────────────
   SLUG + SHORT CODE UTILITIES
───────────────────────────────────────────────────────────────────────── */

/** "Eleanor Grace Thompson" → "eleanor-grace-thompson" */
export function nameToSlug(name = '') {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 80);
}

/** Generates a compact 8-char alphanumeric short code */
export function generateShortCode(memorialId = '') {
  const base = memorialId.replace(/[^a-z0-9]/gi, '').slice(0, 3).toUpperCase();
  const rand  = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `${base}${rand}`.slice(0, 8);
}

/**
 * Name-slug share URL — human readable, deep-linkable.
 * MemorialDetail.resolveMemorial() reads ?id= first so the exact page loads.
 * Pattern: <origin>/#/memorial/<slug>?id=<memorialId>
 */
export function buildSlugUrl(memorialName, memorialId) {
  const slug = nameToSlug(memorialName);
  return `${window.location.origin}/#/memorial/${encodeURIComponent(slug)}?id=${encodeURIComponent(memorialId)}`;
}

/**
 * Short-code URL.
 * Pattern: <origin>/#/m/<shortCode>
 * ShortLinkRedirect in App.jsx resolves this → /#/memorial/<slug>?id=<memorialId>
 */
export function buildShortUrl(shortCode) {
  return `${window.location.origin}/#/m/${shortCode}`;
}

/* ─────────────────────────────────────────────────────────────────────────
   LOCAL-STORAGE CACHE
   Dual-indexed: by shortCode AND by memorialId (mid: prefix).

   Schema (each stored value):
     { shortCode, memorialId, memorialName, slug, dbId, createdAt }
───────────────────────────────────────────────────────────────────────── */

const LS_KEY = 'msanctuary_short_links';

function lsGetAll() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); }
  catch { return {}; }
}

/**
 * Persist record — always normalise slug so lookups never get an empty string.
 */
function lsSave(record) {
  try {
    const slug = record.slug && record.slug.trim()
      ? record.slug
      : nameToSlug(record.memorialName || record.memorial_name || '');

    const normalised = { ...record, slug };

    const all = lsGetAll();
    all[normalised.shortCode]           = normalised;
    all[`mid:${normalised.memorialId}`] = normalised;
    localStorage.setItem(LS_KEY, JSON.stringify(all));
  } catch { /* quota exceeded — safe to ignore */ }
}

function lsFindByShortCode(shortCode) {
  return lsGetAll()[shortCode] ?? null;
}

function lsFindByMemorialId(memorialId) {
  return lsGetAll()[`mid:${memorialId}`] ?? null;
}

/* ─────────────────────────────────────────────────────────────────────────
   NCB TABLE PROBE  (best-effort, never throws)
───────────────────────────────────────────────────────────────────────── */

const TABLE       = 'memorial_short_links';
let _ncbAvailable = null; // null = unknown | true | false

async function checkNcbAvailable() {
  if (_ncbAvailable !== null) return _ncbAvailable;
  try {
    const res  = await writeFetch(`read/${TABLE}?limit=1`);
    const data = await res.json();
    _ncbAvailable = data.status === 'success';
  } catch {
    _ncbAvailable = false;
  }
  return _ncbAvailable;
}

/* ─────────────────────────────────────────────────────────────────────────
   PUBLIC API
───────────────────────────────────────────────────────────────────────── */

/**
 * getOrCreateShortLink
 *
 * Resolution order:
 *   1. localStorage  (instant, same-device)
 *   2. NCB database  (cross-device, if table exists)
 *   3. Generate new  → persist to both LS + NCB
 *
 * Always returns { slug, shortCode, slugUrl, shortUrl, dbId }
 */
export async function getOrCreateShortLink(memorialId, memorialName) {
  const slug    = nameToSlug(memorialName);
  const slugUrl = buildSlugUrl(memorialName, memorialId);

  /* 1 ── localStorage ──────────────────────────────────────────────── */
  const cached = lsFindByMemorialId(memorialId);
  if (cached?.shortCode) {
    if (!cached.slug) lsSave({ ...cached, memorialName, slug });
    return {
      slug:      cached.slug || slug,
      shortCode: cached.shortCode,
      slugUrl,
      shortUrl:  buildShortUrl(cached.shortCode),
      dbId:      cached.dbId ?? null,
    };
  }

  /* 2 ── NCB ───────────────────────────────────────────────────────── */
  const ncbOk = await checkNcbAvailable();

  if (ncbOk) {
    try {
      const readRes  = await writeFetch(
        `read/${TABLE}?memorial_id=${encodeURIComponent(memorialId)}&limit=1`
      );
      const readData = await readRes.json();

      if (readData.status === 'success' && readData.data?.length > 0) {
        const rec    = readData.data[0];
        const record = {
          shortCode:    rec.short_code,
          memorialId:   rec.memorial_id,
          memorialName: rec.memorial_name || memorialName,
          slug:         rec.slug || slug,
          dbId:         rec.id,
          createdAt:    rec.created_at,
        };
        lsSave(record);
        return {
          slug:      record.slug,
          shortCode: record.shortCode,
          slugUrl,
          shortUrl:  buildShortUrl(record.shortCode),
          dbId:      record.dbId,
        };
      }
    } catch { /* fall through to generate */ }
  }

  /* 3 ── Generate new ──────────────────────────────────────────────── */
  const shortCode = generateShortCode(memorialId);
  const session   = getSession();
  let   dbId      = null;

  if (ncbOk) {
    try {
      dbId = crypto.randomUUID();
      await writeFetch(`create/${TABLE}`, {
        method: 'POST',
        body: JSON.stringify({
          id:            dbId,
          memorial_id:   memorialId,
          memorial_name: memorialName,
          slug,
          short_code:    shortCode,
          owner_id:      session?.id ?? null,
          click_count:   0,
        }),
      });
    } catch { dbId = null; }
  }

  lsSave({ shortCode, memorialId, memorialName, slug, dbId, createdAt: new Date().toISOString() });

  return { slug, shortCode, slugUrl, shortUrl: buildShortUrl(shortCode), dbId };
}

/**
 * resolveShortCode
 *
 * Resolution order:
 *   1. localStorage  (instant, same-device)
 *   2. NCB database  (cross-device)
 *
 * ── GUARANTEED RETURN SHAPE ──────────────────────────────────────────────
 * {
 *   memorial_id:   string          ← always non-empty when found
 *   memorial_name: string          ← always non-empty when found
 *   slug:          string          ← derived from memorial_name when missing
 *   slugUrl:       string          ← FULL /#/memorial/<slug>?id=<id> URL
 *                                     ready for navigate() or window.location
 *   id:            string | null   ← NCB row id (click tracking)
 *   click_count:   number
 * }
 * Returns null only when the short code is genuinely unknown.
 * ─────────────────────────────────────────────────────────────────────────
 */
export async function resolveShortCode(shortCode) {
  if (!shortCode) return null;

  /**
   * normalise — converts any raw LS or NCB record into the guaranteed shape.
   *
   * Key guarantee: `slugUrl` is ALWAYS constructed here using `buildSlugUrl`,
   * so callers (ShortLinkRedirect) never have to reconstruct the destination
   * URL manually and risk building a malformed path.
   */
  const normalise = (raw) => {
    const memorialId   = raw.memorialId   || raw.memorial_id   || '';
    const memorialName = raw.memorialName || raw.memorial_name || '';

    // Derive slug — never leave it empty
    const slug = (raw.slug && raw.slug.trim())
      ? raw.slug
      : nameToSlug(memorialName);

    // Build the canonical destination URL directly from the data we have.
    // buildSlugUrl produces: <origin>/#/memorial/<slug>?id=<memorialId>
    // MemorialDetail reads ?id= first, so even an imperfect slug is fine.
    const slugUrl = (memorialName && memorialId)
      ? buildSlugUrl(memorialName, memorialId)
      : memorialId
        ? `${window.location.origin}/#/memorial/${encodeURIComponent(slug || memorialId)}?id=${encodeURIComponent(memorialId)}`
        : null;

    return {
      memorial_id:   memorialId,
      memorial_name: memorialName,
      slug,
      slugUrl,                          // ← NEW: pre-built destination URL
      id:            raw.id   || raw.dbId || null,
      click_count:   raw.click_count ?? 0,
    };
  };

  /* 1 ── localStorage (fastest path) ──────────────────────────────── */
  const cached = lsFindByShortCode(shortCode);
  if (cached?.memorialId || cached?.memorial_id) {
    return normalise(cached);
  }

  /* 2 ── NCB (cross-device) ───────────────────────────────────────── */
  const ncbOk = await checkNcbAvailable();
  if (!ncbOk) return null;

  try {
    const res  = await writeFetch(
      `read/${TABLE}?short_code=${encodeURIComponent(shortCode)}&limit=1`
    );
    const data = await res.json();

    if (data.status === 'success' && data.data?.length > 0) {
      const rec = data.data[0];
      // Hydrate localStorage so subsequent navigations are instant
      lsSave({
        shortCode,
        memorialId:   rec.memorial_id,
        memorialName: rec.memorial_name,
        slug:         rec.slug,
        dbId:         rec.id,
        createdAt:    rec.created_at,
      });
      return normalise(rec);
    }
  } catch { /* silent */ }

  return null;
}

/**
 * trackShortLinkClick — fire-and-forget analytics, never blocks navigation
 */
export async function trackShortLinkClick(dbId, currentCount = 0) {
  if (!dbId) return;
  try {
    await writeFetch(`update/${TABLE}/${dbId}`, {
      method: 'PUT',
      body: JSON.stringify({ click_count: (currentCount ?? 0) + 1 }),
    });
  } catch { /* silent */ }
}